/**
 * FormBody — конструктор формы: добавление полей, типы, опции для select/radio/checkbox.
 * Drag-reorder в этой итерации отсутствует — порядок управляется кнопками ↑↓
 * (drag будет добавлен в финальной отшлифовке всех редакторов вместе).
 */
import { defaultTemplateMeta } from '@inbx/shared/schemas';
import { type ReactNode } from 'react';

import { type WidgetBodyContract, type WidgetDraft } from './editor-shell';
import { BehaviorTab, CodeTab, ThemeTab } from './shared-tabs';

type FieldType = 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'file';

interface FormField {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  options?: string[]; // для select/radio/checkbox
}

interface FormContent {
  title?: string;
  subtitle?: string;
  fields?: FormField[];
  submit_label?: string;
  success_message?: string;
}

export function makeFormBody(args: {
  widgetPublicKey: string;
  workspaceId: string;
}): WidgetBodyContract {
  return {
    getTabs() {
      return [
        { key: 'content', label: 'Поля' },
        { key: 'theme', label: 'Тема' },
        { key: 'behavior', label: 'Поведение' },
        { key: 'code', label: 'Код' },
      ];
    },
    defaultDraft() {
      // Старт с реального пресета из общего реестра (структурно «настоящего»).
      const t = defaultTemplateMeta('form');
      return {
        themeConfig: { template_id: t.id },
        contentConfig: { ...(t.defaultContent as WidgetDraft['contentConfig']) },
        behaviorConfig: { ...(t.defaultBehavior ?? {}) } as WidgetDraft['behaviorConfig'],
        mediaConfig: {},
      };
    },
    validate(draft) {
      const errs: string[] = [];
      const c = (draft.contentConfig ?? {}) as FormContent;
      if (!c.title?.trim()) errs.push('Заголовок формы обязателен');
      if (!c.fields?.length) errs.push('Добавьте хотя бы одно поле');
      for (const f of c.fields ?? []) {
        if (!f.key.trim()) errs.push(`Поле "${f.label}" без ключа`);
        if ((f.type === 'select' || f.type === 'radio' || f.type === 'checkbox') && (!f.options || f.options.length === 0)) {
          errs.push(`Поле "${f.label}" типа "${f.type}" нуждается в опциях`);
        }
      }
      return errs;
    },
    renderTab(activeKey, draft, onChange): ReactNode {
      if (activeKey === 'content') return <FormFieldsTab draft={draft} onChange={onChange} />;
      if (activeKey === 'theme') return <ThemeTab draft={draft} onChange={onChange} />;
      if (activeKey === 'behavior') return <BehaviorTab draft={draft} onChange={onChange} />;
      if (activeKey === 'code')
        return <CodeTab widgetPublicKey={args.widgetPublicKey} workspaceId={args.workspaceId} />;
      return null;
    },
  };
}

const FIELD_TYPES: Array<{ value: FieldType; label: string }> = [
  { value: 'text', label: 'Текст' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Телефон' },
  { value: 'textarea', label: 'Многострочный' },
  { value: 'select', label: 'Выпадающий список' },
  { value: 'radio', label: 'Один из вариантов' },
  { value: 'checkbox', label: 'Несколько вариантов' },
  { value: 'file', label: 'Загрузка файла' },
];

function FormFieldsTab({
  draft,
  onChange,
}: {
  draft: WidgetDraft;
  onChange: (patch: Partial<WidgetDraft>) => void;
}): JSX.Element {
  const c = (draft.contentConfig ?? {}) as FormContent;
  const fields = c.fields ?? [];
  const setContent = (patch: Partial<FormContent>): void => {
    onChange({ contentConfig: { ...draft.contentConfig, ...patch } });
  };
  const setFields = (next: FormField[]): void => setContent({ fields: next });
  const updateField = (idx: number, patch: Partial<FormField>): void => {
    setFields(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const move = (idx: number, dir: -1 | 1): void => {
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setFields(next);
  };
  const remove = (idx: number): void => setFields(fields.filter((_, i) => i !== idx));
  const add = (): void => {
    setFields([
      ...fields,
      { key: `field_${Date.now()}`, label: 'Новое поле', type: 'text' },
    ]);
  };

  return (
    <div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Заголовки</div>
        <div className="inbx-editor-row">
          <div>
            <label className="inbx-editor-label">Заголовок</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.title ?? ''}
              onChange={(e) => setContent({ title: e.target.value })}
            />
          </div>
          <div>
            <label className="inbx-editor-label">Подзаголовок</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.subtitle ?? ''}
              onChange={(e) => setContent({ subtitle: e.target.value })}
            />
          </div>
        </div>
        <div className="inbx-editor-row">
          <div>
            <label className="inbx-editor-label">Текст кнопки отправки</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.submit_label ?? ''}
              onChange={(e) => setContent({ submit_label: e.target.value })}
            />
          </div>
          <div>
            <label className="inbx-editor-label">Сообщение после отправки</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.success_message ?? ''}
              onChange={(e) => setContent({ success_message: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Поля формы</div>
        <div className="inbx-editor-help" style={{ marginBottom: 8 }}>
          Порядок: drag-and-drop за «⋮⋮» слева, либо стрелки ↑↓ справа.
        </div>
        {fields.length === 0 && <div className="inbx-editor-help">Нет полей — добавьте.</div>}
        {fields.map((f, idx) => (
          <FieldEditor
            key={f.key + idx}
            field={f}
            onChange={(p) => updateField(idx, p)}
            onMove={(dir) => move(idx, dir)}
            onRemove={() => remove(idx)}
            isFirst={idx === 0}
            isLast={idx === fields.length - 1}
            onDragSwap={(from, to) => {
              if (from === to || from < 0 || to < 0 || from >= fields.length || to >= fields.length)
                return;
              const next = [...fields];
              const [picked] = next.splice(from, 1);
              if (picked) next.splice(to, 0, picked);
              setFields(next);
            }}
            index={idx}
          />
        ))}
        <button type="button" className="inbx-btn" onClick={add} style={{ marginTop: 8 }}>
          + Добавить поле
        </button>
      </div>
    </div>
  );
}

function FieldEditor({
  field,
  onChange,
  onMove,
  onRemove,
  isFirst,
  isLast,
  index,
  onDragSwap,
}: {
  field: FormField;
  onChange: (patch: Partial<FormField>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  isFirst: boolean;
  isLast: boolean;
  index: number;
  onDragSwap: (from: number, to: number) => void;
}): JSX.Element {
  const needsOptions =
    field.type === 'select' || field.type === 'radio' || field.type === 'checkbox';
  const opts = field.options ?? [];
  const setOpt = (idx: number, val: string): void => {
    onChange({ options: opts.map((o, i) => (i === idx ? val : o)) });
  };
  const addOpt = (): void => onChange({ options: [...opts, `Вариант ${opts.length + 1}`] });
  const removeOpt = (idx: number): void =>
    onChange({ options: opts.filter((_, i) => i !== idx) });
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData('text/plain'));
        if (Number.isFinite(from)) onDragSwap(from, index);
      }}
      style={{
        border: '1px solid var(--inbx-border)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
        background: 'var(--inbx-bg-secondary)',
        position: 'relative',
        paddingLeft: 28,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 8,
          top: 14,
          color: 'var(--inbx-text-tertiary)',
          cursor: 'grab',
          userSelect: 'none',
          fontSize: 16,
          lineHeight: 1,
        }}
        title="Перетащить"
      >
        ⋮⋮
      </div>
      <div className="inbx-editor-row">
        <div>
          <label className="inbx-editor-label">Метка</label>
          <input
            type="text"
            className="inbx-editor-input"
            value={field.label}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </div>
        <div>
          <label className="inbx-editor-label">Ключ (для БД)</label>
          <input
            type="text"
            className="inbx-editor-input"
            value={field.key}
            onChange={(e) => onChange({ key: e.target.value.replace(/[^a-z0-9_]/gi, '_') })}
          />
        </div>
      </div>
      <div className="inbx-editor-row">
        <div>
          <label className="inbx-editor-label">Тип</label>
          <select
            className="inbx-editor-select"
            value={field.type}
            onChange={(e) =>
              onChange({ type: e.target.value as FieldType, options: undefined })
            }
          >
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="inbx-editor-label">Подсказка</label>
          <input
            type="text"
            className="inbx-editor-input"
            value={field.placeholder ?? ''}
            onChange={(e) => onChange({ placeholder: e.target.value })}
          />
        </div>
        <div style={{ flex: 0, alignSelf: 'flex-end', display: 'flex', gap: 4 }}>
          <button
            type="button"
            className="inbx-btn"
            disabled={isFirst}
            onClick={() => onMove(-1)}
            title="Выше"
          >
            ↑
          </button>
          <button
            type="button"
            className="inbx-btn"
            disabled={isLast}
            onClick={() => onMove(1)}
            title="Ниже"
          >
            ↓
          </button>
          <button
            type="button"
            className="inbx-btn inbx-btn--danger"
            onClick={onRemove}
            title="Удалить"
          >
            ✕
          </button>
        </div>
      </div>
      <label
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          checked={field.required ?? false}
          onChange={(e) => onChange({ required: e.target.checked })}
        />
        Обязательное
      </label>

      {needsOptions && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--inbx-border)' }}>
          <label className="inbx-editor-label">Опции</label>
          {opts.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
              <input
                type="text"
                className="inbx-editor-input"
                value={o}
                onChange={(e) => setOpt(i, e.target.value)}
              />
              <button
                type="button"
                className="inbx-btn inbx-btn--danger"
                onClick={() => removeOpt(i)}
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="inbx-btn" onClick={addOpt}>
            + Опция
          </button>
        </div>
      )}
    </div>
  );
}
